import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Stack, Skeleton, Card, Chip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import DashboardSectionHeader from '../components/DashboardSectionHeader';
import AreaTrendCard from '../components/charts/AreaTrendCard';
import DonutCard from '../components/charts/DonutCard';
import HorizontalBarCard from '../components/charts/HorizontalBarCard';
import KpiTile from '../components/charts/KpiTile';
import { ShoppingBag, Users as UsersIcon, Briefcase, MessageSquare, FileText, MessageCircle } from 'lucide-react';
import { fetchAdvancedAnalytics, setWindowDays } from '../redux/slices/analyticsSlice';
import PageHeader from '../components/common/PageHeader';

const RANGES = [
  { label: '7d', value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
  { label: '180d', value: 180 },
];

const gridItemSx = {
  display: 'flex',
  '& > *': { flex: 1, width: '100%', minWidth: 0 },
};

const AnalyticsView = () => {
  const dispatch = useDispatch();
  const { data, isLoading, windowDays } = useSelector((s) => s.analytics);
  const [trend, setTrend] = useState('products');

  useEffect(() => {
    dispatch(fetchAdvancedAnalytics(windowDays));
  }, [dispatch, windowDays]);

  const series = data?.series || {};
  const growth = data?.growth || {};
  const totals = data?.totals || {};
  const win = data?.window || {};
  const breakdowns = data?.breakdowns || {};

  const segmentKpis = [
    { label: 'New Products', value: (win.products || 0).toLocaleString(), icon: <ShoppingBag size={20} />, color: '#FE5F01', growth: growth.products },
    { label: 'New Users', value: (win.users || 0).toLocaleString(), icon: <UsersIcon size={20} />, color: '#7C3AED', growth: growth.users },
    { label: 'New Businesses', value: (win.businesses || 0).toLocaleString(), icon: <Briefcase size={20} />, color: '#D97706', growth: growth.businesses },
    // Enquiries submitted vs leads delivered are different quantities — one
    // enquiry fans out to several leads, unmatched enquiries produce none.
    { label: 'New Buyer Enquiries', value: (win.enquiries || 0).toLocaleString(), icon: <MessageSquare size={20} />, color: '#DC2626', growth: growth.enquiries },
    { label: 'Leads Delivered', value: (win.leads || 0).toLocaleString(), icon: <MessageSquare size={20} />, color: '#B91C1C', growth: growth.leads },
    { label: 'New RFQs', value: (win.rfqs || 0).toLocaleString(), icon: <FileText size={20} />, color: '#0284C7' },
    { label: 'New Chats', value: (win.conversations || 0).toLocaleString(), icon: <MessageCircle size={20} />, color: '#2563EB' },
  ];

  // Funnel: users → intent signals → demand submitted → leads delivered
  const funnel = [
    { name: 'Total Users', count: totals.users || 0 },
    { name: 'Wishlists Saved', count: totals.wishlists || 0 },
    { name: 'Chats Started', count: totals.conversations || 0 },
    { name: 'RFQs Posted', count: totals.rfqs || 0 },
    { name: 'Buyer Enquiries', count: totals.enquiries || 0 },
    { name: 'Leads Delivered', count: totals.leads || 0 },
  ];

  // Which channel each delivered lead came through.
  const leadsBySource = breakdowns.leadsBySource || [];

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <PageHeader
        title="Analytics"
        subtitle="Deep segmentation, growth funnels, channel attribution, and regional marketplace insights."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Analytics' }
        ]}
      />

      <DashboardSectionHeader
        title="Time range"
        subtitle="Filter all charts and KPIs below"
        chip={data?.isMock ? (
          <Chip size="small" label="Sample data" color="warning" sx={{ fontWeight: 700 }} />
        ) : null}
        ranges={RANGES}
        rangeValue={windowDays}
        onRangeChange={(v) => dispatch(setWindowDays(v))}
        onRefresh={() => dispatch(fetchAdvancedAnalytics(windowDays))}
      />

      <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ width: '100%', mt: { xs: 0.5, md: 1 }, mb: 3 }}>
        {segmentKpis.map((k) => (
          <Grid size={{ xs: 6, sm: 6, md: 4, lg: 2 }} key={k.label} sx={gridItemSx}>
            {isLoading && !data ? (
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
            ) : (
              <KpiTile {...k} />
            )}
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', mt: { xs: 1.5, md: 2.5 }, mb: 3 }}>
        <Grid size={12} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={420} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <AreaTrendCard
              title="Multi-segment Growth"
              subtitle={`Switch between segments — last ${windowDays} days`}
              seriesMap={series}
              growth={growth}
              activeKey={trend}
              onSelect={setTrend}
              height={{ xs: 280, md: 380 }}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', mt: { xs: 1.5, md: 2.5 }, mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={360} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <DonutCard
              title="Category Distribution"
              subtitle="Share of catalog by category"
              data={breakdowns.productsByCategory || []}
              height={{ xs: 260, md: 300 }}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={360} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, height: '100%', width: '100%', maxWidth: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Conversion Funnel</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Activity progression across the platform
              </Typography>
              <Stack spacing={1.25}>
                {funnel.map((f, i) => {
                  const top = funnel[0].count || 1;
                  const pct = Math.round((f.count / top) * 100);
                  return (
                    <Box key={f.name}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                          {(f.count || 0).toLocaleString()} · {pct}%
                        </Typography>
                      </Stack>
                      <Box sx={{
                        height: 12, borderRadius: 6, mt: 0.5,
                        bgcolor: 'action.hover',
                        position: 'relative', overflow: 'hidden',
                      }}>
                        <Box sx={{
                          position: 'absolute', inset: 0, width: `${pct}%`,
                          background: `linear-gradient(90deg, hsl(${180 - i * 20}, 70%, 45%), hsl(${180 - i * 20}, 70%, 55%))`,
                          borderRadius: 6,
                        }} />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Card>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', mt: { xs: 1.5, md: 2.5 } }}>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={420} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <HorizontalBarCard title="Top Cities by Listings" data={(breakdowns.productsByCity || []).slice(0, 10)} />
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={420} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <HorizontalBarCard title="Top States by Listings" data={(breakdowns.productsByState || []).slice(0, 10)} />
          )}
        </Grid>
      </Grid>

      {/* Which channel actually delivers leads. "Untracked (legacy)" is data
          from before channel attribution existed. */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', mt: { xs: 1.5, md: 2.5 } }}>
        <Grid size={{ xs: 12 }} sx={gridItemSx}>
          {isLoading && !data ? (
            <Skeleton variant="rounded" height={420} sx={{ borderRadius: 4, width: '100%' }} />
          ) : (
            <HorizontalBarCard title="Leads Delivered by Channel" data={leadsBySource} />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsView;
